import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  UnorderedList,
  ListItem,
  AlertDialogFooter,
  Button,
  Text,
  useToast,
  Tooltip,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { apiRequest } from "utils/api";
import { TeamMember } from "./TeamMemberList";
import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export function DeactiveMemberDialog({
  userToDeactivate,
  onSuccess,
}: {
  userToDeactivate: TeamMember;
  onSuccess: () => void;
}) {
  const cancelRef = useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleDeactivate = async (userId: string) => {
    try {
      setIsLoading(true);
      await apiRequest("/supabase/users/delete", { id: userId });
      toast({
        title: "User deactivated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Deactivation Failed",
        description: error.message || "Failed to deactivate user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip label="Deactivate user">
        <IconButton
          aria-label="Deactivate user"
          icon={<FontAwesomeIcon icon="user-slash" />}
          variant="ghost"
          colorScheme="red"
          size="sm"
          onClick={onOpen}
        />
      </Tooltip>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="2xl">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deactivate User Access
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to deactivate access for {userToDeactivate?.first_name}{" "}
              {userToDeactivate?.last_name}?
              <Text mt={6} fontWeight="medium">
                This will:
              </Text>
              <UnorderedList mt={2}>
                <ListItem>Prevent the user from logging in</ListItem>
                <ListItem>Keep all their data and history intact</ListItem>
                <ListItem>
                  Preserve all their relationships with loans, documents, and other records
                </ListItem>
                <ListItem>Not delete any database records</ListItem>
              </UnorderedList>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => userToDeactivate && handleDeactivate(userToDeactivate.id)}
                ml={3}
                isLoading={isLoading}
              >
                Deactivate Access
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
